import React, { useState, useEffect, useCallback, Suspense, lazy } from 'react';
const CanvasBoard = lazy(() => import('./components/CanvasBoard'));
const ViewBoardPage = lazy(() => import('./components/ViewBoardPage').then(module => ({ default: module.ViewBoardPage })));
// Import Mobile Shell lazily to avoid bloating desktop bundle
const MobileShell = lazy(() => import('./components/mobile').then(m => ({ default: m.MobileShell })));
import { useDeviceDetection } from './components/mobile';
import HomePage from './components/HomePage';
import Onboarding from './components/Onboarding';
import { LandingPage } from './components/landing/LandingPage';
import { CommandPalette } from './components/CommandPalette';
import { BoardTemplate } from './templates';
import { Template } from './utils/templates';
import { Board, CanvasSettings, UserProfile } from './types';
import { LoadingScreen } from './components/ui/LoadingScreen';
import { useTheme } from './contexts/ThemeContext';
import * as AuthService from './services/authService';
import { TitleBar } from './components/TitleBar';
import { supabase } from './services/supabase';
import ErrorBoundary from './components/ErrorBoundary';
import { BoardNotFoundFallback } from './components/BoardNotFoundFallback';
import { useWorkspaces } from './hooks/useWorkspaces';
import { useAppInit } from './hooks/app/useAppInit';
import { useAppRouting } from './hooks/app/useAppRouting';
import { useBoardOperations } from './hooks/app/useBoardOperations';
import { HelpCenter } from './components/help/HelpCenter';
import { ArticleView } from './components/help/ArticleView';
import { HELP_DATA } from './data/helpData';
import { PrivacyPolicy } from './components/help/PrivacyPolicy';
// Content Pages
import { UseCasePage } from './components/content/UseCasePage';
import { BlogPostPage } from './components/content/BlogPage';
import { BlogHomePage } from './components/content/BlogHomePage';
import { BLOG_POSTS } from './data/blogPosts';
import { DownloadPage } from './components/landing/DownloadPage';
import { FeaturePage } from './components/content/FeaturePage';
import { ComparePage } from './components/content/ComparePage';
import { FEATURE_PAGES, COMPARISON_PAGES, USE_CASE_PAGES } from './data/seoPages';

const SETTINGS_KEY = 'notespace_canvas_settings_v1';
const PROFILE_KEY = 'notespace_profile_v1';

function App() {
  // Device Detection (Must be top-level)
  const { isMobile } = useDeviceDetection();

  // --- Custom Hooks (Refactored Logic) ---
  const {
    isLoading,
    userProfile,
    setUserProfile,
    boards,
    setBoards
  } = useAppInit();

  const {
    viewToken,
    contentPath,
    activeBoardId,
    setActiveBoardId,
    boardNotFound,
    setBoardNotFound,
    isHelpPage,
    setIsHelpPage,
    activeHelpArticleId,
    setActiveHelpArticleId,
    isPrivacyPage,
    setIsPrivacyPage,
    setContentPath
  } = useAppRouting(boards, setBoards, isLoading);

  // --- Theme & Settings ---
  const { theme, setTheme, toggleTheme } = useTheme();

  const [canvasSettings, setCanvasSettings] = useState<CanvasSettings>(() => {
    const saved = localStorage.getItem(SETTINGS_KEY);
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.error("Failed to parse settings", e);
      }
    }
    return {
      snapToGrid: false,
      showGuides: true,
      gridSize: 20
    };
  });

  useEffect(() => {
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(canvasSettings));
  }, [canvasSettings]);

  // --- Workspaces ---
  const {
    workspaces,
    activeWorkspaceId,
    setActiveWorkspaceId,
    createWorkspace,
    deleteWorkspace,
    renameWorkspace,
    addBoardToWorkspace,
    WORKSPACE_COLORS,
  } = useWorkspaces();

  // --- Board Operations ---
  const {
    handleCreateBoard,
    handleUpdateBoard,
    handleBoardSync,
    handleDeleteBoard,
    handleRestoreBoard,
    handleDeleteForever,
    handleToggleFavorite,
    handleDuplicateBoard,
    handleImportBoard,
    saveBoard
  } = useBoardOperations({
    boards,
    setBoards,
    activeBoardId,
    setActiveBoardId,
    activeWorkspaceId,
    workspaces,
    addBoardToWorkspace
  });

  // --- UI State ---
  const [isCommandPaletteOpen, setIsCommandPaletteOpen] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);

  // Listen for Ctrl+K
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setIsCommandPaletteOpen(prev => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Toggle theme for command palette
  const handleToggleTheme = useCallback(() => {
    toggleTheme();
  }, [toggleTheme]);

  const handleUpdateProfile = useCallback((updatedProfile: Partial<UserProfile>) => {
    if (userProfile) {
      const newProfile = { ...userProfile, ...updatedProfile };
      setUserProfile(newProfile);
      AuthService.updateProfile(userProfile.id, updatedProfile);
    }
  }, [userProfile, setUserProfile]);

  const handleLogout = useCallback(async () => {
    try {
      await supabase.auth.signOut();
    } catch (error) {
      console.error('Error logging out of Supabase:', error);
    } finally {
      AuthService.signOut();
      setBoards([]);
      setUserProfile(null);
      setActiveBoardId(null);
      localStorage.removeItem(PROFILE_KEY);
    }
  }, [setBoards, setUserProfile, setActiveBoardId]);

  const handleOnboardingComplete = async (profile: UserProfile, template?: BoardTemplate) => {
    setUserProfile(profile);
    localStorage.setItem(PROFILE_KEY, JSON.stringify(profile));

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        await supabase.from('profiles').upsert({
          id: user.id,
          full_name: profile.name,
          avatar_url: profile.avatar,
          email: profile.email,
          username: profile.name.toLowerCase().replace(/\s+/g, '') + '-' + Math.floor(Math.random() * 1000)
        });
      }
    } catch (e) {
      console.error("Failed to save profile during onboarding", e);
    }

    setShowOnboarding(false);

    if (template) {
      handleCreateBoard(template);
    }
  };

  // --- Render ---

  if (viewToken) {
    return (
      <ViewBoardPage
        token={viewToken}
      />
    );
  }

  // SEO / Content Pages
  if (contentPath) {
    const path = contentPath;

    // Use Cases
    if (path.startsWith('/use-cases/')) {
      // ... existing render logic for use cases
      const slug = path.replace('/use-cases/', '');
      const pageData = USE_CASE_PAGES.find(p => p.slug === slug);
      if (pageData) {
        return (
          <UseCasePage
            {...pageData}
            onGetStarted={() => { setContentPath(null); window.history.pushState({}, '', '/'); }}
          />
        );
      }
    }

    // Blog logic
    if (path === '/blog') {
      return (
        <BlogHomePage
          onNavigateHome={() => { setContentPath(null); window.history.pushState({}, '', '/'); }}
          onNavigateToPost={(slug) => {
            setContentPath(`/blog/${slug}`);
            window.history.pushState({}, '', `/blog/${slug}`);
            window.scrollTo(0, 0);
          }}
          onGetStarted={() => { setContentPath(null); window.history.pushState({}, '', '/'); }}
        />
      );
    }
    if (path.startsWith('/blog/')) {
      const slug = path.replace('/blog/', '');
      const post = BLOG_POSTS.find(p => p.slug === slug);
      if (post) {
        return (
          <BlogPostPage
            {...post}
            onGetStarted={() => { setContentPath(null); window.history.pushState({}, '', '/'); }}
          />
        );
      }
    }

    // Features
    if (path.startsWith('/features/')) {
      const slug = path.replace('/features/', '');
      const pageData = FEATURE_PAGES.find(p => p.slug === slug);
      if (pageData) {
        return (
          <FeaturePage
            {...pageData}
            onGetStarted={() => { setContentPath(null); window.history.pushState({}, '', '/'); }}
          />
        );
      }
    }

    // Compare
    if (path.startsWith('/compare/')) {
      const slug = path.replace('/compare/', '');
      const pageData = COMPARISON_PAGES.find(p => p.slug === slug);
      if (pageData) {
        return (
          <ComparePage
            {...pageData}
            onGetStarted={() => { setContentPath(null); window.history.pushState({}, '', '/'); }}
          />
        );
      }
    }
  }

  // Mobile View
  if (isMobile) {
    return (
      <Suspense fallback={<div className="flex items-center justify-center h-screen bg-slate-900 text-white">Loading mobile experience...</div>}>
        <MobileShell
          boards={boards}
          initialBoardId={activeBoardId}
          onSelectWorkspace={setActiveWorkspaceId}
          onCreateBoard={() => handleCreateBoard()}
          onUpdateBoard={handleUpdateBoard}
          onDeleteBoard={handleDeleteBoard}
          onToggleFavorite={handleToggleFavorite}
          onLogout={handleLogout}
          userProfile={userProfile}
          currentTheme={theme}
          onThemeChange={setTheme}
          workspaces={workspaces}
          activeWorkspaceId={activeWorkspaceId}
        />
      </Suspense>
    );
  }

  // Help Center
  if (isHelpPage) {
    if (activeHelpArticleId) {
      const article = HELP_DATA.articles.find(a => a.id === activeHelpArticleId);
      if (article) return <ArticleView article={article} onBack={() => window.history.back()} onGetStarted={() => setShowOnboarding(true)} />;
    }
    return (
      <HelpCenter
        onSelectArticle={(id) => window.history.pushState({}, '', `/help/${id}`)}
        onBack={() => { setIsHelpPage(false); window.history.pushState({}, '', '/'); }}
        onGetStarted={() => setShowOnboarding(true)}
        onPrivacyPolicy={() => {
          setIsPrivacyPage(true);
          setIsHelpPage(false);
          window.history.pushState({}, '', '/privacy');
        }}
      />
    );
  }

  if (isPrivacyPage) {
    return <PrivacyPolicy onBack={() => { setIsPrivacyPage(false); window.history.pushState({}, '', '/'); }} onGetStarted={() => setShowOnboarding(true)} />;
  }

  // Loading Screen
  if (isLoading) {
    return <LoadingScreen />;
  }

  const activeBoard = boards.find(b => b.id === activeBoardId);

  // Landing Page (if no user and no board and not loading)
  if (!userProfile && !activeBoardId && boards.length === 0 && !showOnboarding) {
    return <LandingPage onGetStarted={() => setShowOnboarding(true)} />;
  }

  // Onboarding
  if (showOnboarding) {
    return <Onboarding onComplete={handleOnboardingComplete} currentTheme={theme} onThemeChange={setTheme} />;
  }

  // Main App Shell
  return (
    <div className={`flex flex-col h-screen w-full overflow-hidden ${theme === 'dark' ? 'dark bg-[#0F0F11]' : 'bg-gray-50'}`}>
      <TitleBar />

      {/* Command Palette */}
      <CommandPalette
        isOpen={isCommandPaletteOpen}
        onClose={() => setIsCommandPaletteOpen(false)}
        boards={boards}
        onSelectBoard={(id) => {
          setActiveBoardId(id);
          setIsCommandPaletteOpen(false);
          // Also set view state to canvas if opening a board
        }}
        onCreateBoard={() => {
          setIsCommandPaletteOpen(false);
          handleCreateBoard();
        }}
        onToggleTheme={handleToggleTheme}
        currentView={activeBoardId ? 'canvas' : 'home'}
        onGoHome={() => {
          setActiveBoardId(null);
          setIsCommandPaletteOpen(false);
          window.history.pushState({}, '', '/');
        }}
      />

      {/* Main Board View */}
      {activeBoardId ? (
        activeBoard ? (
          <ErrorBoundary>
            <Suspense fallback={<div className="flex items-center justify-center h-full text-white/50">Loading board...</div>}>
              <CanvasBoard
                key={activeBoard.id} // Re-mount on board change
                board={activeBoard}
                canvasSettings={canvasSettings}
                onSave={handleUpdateBoard}
                onSync={handleBoardSync}
                user={userProfile}
                onBack={() => {
                  setActiveBoardId(null);
                  window.history.pushState({}, '', '/');
                }}
              />
            </Suspense>
          </ErrorBoundary>
        ) : (
          <BoardNotFoundFallback onGoHome={() => { setBoardNotFound(false); setActiveBoardId(null); window.history.pushState({}, '', '/'); }} />
        )
      ) : (
        <HomePage
          userProfile={userProfile}
          boards={boards}
          workspaces={workspaces}
          activeWorkspaceId={activeWorkspaceId}
          onSelectWorkspace={setActiveWorkspaceId}
          onCreateWorkspace={createWorkspace}
          onRenameWorkspace={renameWorkspace}
          onDeleteWorkspace={deleteWorkspace}
          onCreateBoard={handleCreateBoard}
          onSelectBoard={setActiveBoardId}
          onDeleteBoard={handleDeleteBoard}
          onDuplicateBoard={handleDuplicateBoard}
          onImportBoard={handleImportBoard}
          onToggleFavorite={handleToggleFavorite}
          onDeleteForever={handleDeleteForever}
          onRestoreBoard={handleRestoreBoard}
          onUpdateProfile={handleUpdateProfile}
          onLogout={handleLogout}
          currentTheme={theme}
          onThemeChange={setTheme}
          canvasSettings={canvasSettings}
          onCanvasSettingsChange={setCanvasSettings}
          onUpdateBoard={handleUpdateBoard}
          workspaceColors={WORKSPACE_COLORS}
        />
      )}
    </div>
  );
}

export default App;
